import React, { useState, useEffect } from 'react';
import {
    Typography, Box, Button, IconButton, Tooltip, Grid,
    useTheme, useMediaQuery, TextField, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, CircularProgress, Switch
} from '@mui/material';
import { FiBookOpen, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../api';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import TopicModal from './TopicModal';


const TopicList = () => {
    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [error, setError] = useState('');

    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState({ title: '', exam_id: '', subject_id: '', status: 'Active' });
    const [newTopic, setNewTopic] = useState({ title: '', exam_id: '', subject_id: '', status: 'Active' });
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [filteredSubjects, setFilteredSubjects] = useState([]);

    //for edit modal
    useEffect(() => {
        if (editData?.exam_id) {
            const filtered = subjects.filter(subject => subject.exam_id === editData.exam_id);
            setFilteredSubjects(filtered);
        }
    }, [editData?.exam_id, subjects]);


    //for adding new topic 
    useEffect(() => {
        if (newTopic.exam_id) {
            const filtered = subjects.filter(subject => subject.exam_id === newTopic.exam_id);
            setFilteredSubjects(filtered);
        } else {
            setFilteredSubjects([]);
        }
    }, [newTopic.exam_id, subjects]);

    //use effect for preselected fields when we click on edit

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const examsResponse = await api.get('/exams');
                setExams(examsResponse.data);

                const subjectsResponse = await api.get('/subjects');
                setSubjects(subjectsResponse.data);

                const topicsResponse = await api.get('/topics');
                const transformed = topicsResponse.data.map(topic => {
                    const exam = examsResponse.data.find(e => e.id === topic.exam_id);
                    const subject = subjectsResponse.data.find(s => s.id === topic.subject_id);

                    return {
                        id: topic.id,
                        title: topic.title,
                        examName: exam?.title || 'N/A',
                        subjectName: subject?.name || 'N/A',
                        status: topic.status ? 'Active' : 'Inactive'
                    };
                });

                setTopics(transformed);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);


    const fetchExams = async () => {
        try {
            const res = await api.get('/exams');  // ✅ token included automatically
            setExams(res.data);
            console.log("Fetched exams:", res.data);

        } catch (err) {
            console.error('Failed to fetch exams', err);
        }
    };

    const fetchSubjects = async () => {
        try {
            const res = await api.get('/subjects'); // ✅ using api (token auto added)
            setSubjects(res.data);
        } catch (err) {
            console.error('Failed to fetch subjects', err);
        }
    };


    const fetchTopics = async () => {
        try {
            const response = await api.get('/topics'); // ✅ token included
            const transformed = response.data.map(topic => {
                const exam = exams.find(e => e.id === topic.exam_id);
                const subject = subjects.find(s => s.id === topic.subject_id);

                return {
                    id: topic.id,
                    title: topic.title,
                    examName: exam?.title || 'N/A',
                    subjectName: subject?.name || 'N/A',
                    status: topic.status ? 'Active' : 'Inactive'
                };
            });
            setTopics(transformed);
        } catch (err) {
            console.error('Failed to fetch topics', err);
        } finally {
            setLoading(false);
        }
    };
    const filteredTopics = topics.filter(topic =>
        topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.examName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.subjectName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleAddTopic = async () => {
        if (!newTopic.exam_id || !newTopic.subject_id || !newTopic.title) {
            setError('Please fill all fields');
            return;
        }


        // 🔍 Duplicate check
        const isDuplicate = topics.some(
            (topic) =>
                topic.title.trim().toLowerCase() === newTopic.title.trim().toLowerCase() &&
                topic.examName === exams.find(e => e.id === parseInt(newTopic.exam_id))?.title &&
                topic.subjectName === subjects.find(s => s.id === parseInt(newTopic.subject_id))?.name
        );

        if (isDuplicate) {
            Swal.fire({
                icon: 'error',
                title: 'Duplicate Topic',
                text: 'This topic already exists. Please provide another name.',
            });
            return;
        }
        try {
            const payload = {
                exam_id: parseInt(newTopic.exam_id),
                subject_id: parseInt(newTopic.subject_id),
                title: newTopic.title,
                status: newTopic.status === 1 || newTopic.status === 'Active' ? 1 : 0
            };
            const res = await api.post('/topics', payload);
            const addedTopic = {
                id: res.data.topic?.id ?? res.data.id,
                title: res.data.topic?.title ?? res.data.title,
                exam_id: payload.exam_id,
                subject_id: payload.subject_id,
                status: payload.status === 1 ? 'Active' : 'Inactive'
            };
            setTopics(prev => [...prev, addedTopic]);
            setAddModalOpen(false);
            setNewTopic({ exam_id: '', subject_id: '', title: '', status: 'Active' });
            Swal.fire('Success', 'Topic added!', 'success');
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to add topic', 'error');
        }
    };


    //function for delete icon
    const handleDelete = async (id) => {
        try {
            const confirm = await Swal.fire({
                title: 'Are you sure?',
                text: 'This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            });

            if (confirm.isConfirmed) {
                const res = await api.delete(`/topics/${id}`);

                if (res.status === 200 || res.status === 204) {
                    setTopics(topics.filter(t => t.id !== id));
                    Swal.fire('Deleted!', 'Topic has been deleted.', 'success');
                } else {
                    throw new Error('Failed to delete topic');
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to delete topic';
            Swal.fire('Error', 'Failed to delete topic', 'error');
        }
    };

    const handleEdit = (row) => {
        const exam = exams.find(e => e.id === row.exam_id);
        const subject = subjects.find(s => s.id === row.subject_id);

        setEditData({
            ...row,
            exam_id: exam?.id || '',
            subject_id: subject?.id || '',
            title: row.title || '',
            status: row.status === 'Active' ? 1 : 0,
        });

        setEditModalOpen(true);
    };


    const handleSaveChanges = async () => {
        // 🔍 Check for duplicate topic (same title, exam, and subject)
        const isDuplicate = topics.some(
            (topic) =>
                topic.id !== editData.id && // Exclude the current editing topic
                topic.title.trim().toLowerCase() === editData.title.trim().toLowerCase() &&
                topic.exam_id === parseInt(editData.exam_id) &&
                topic.subject_id === parseInt(editData.subject_id)
        );

        if (isDuplicate) {
            Swal.fire({
                icon: 'error',
                title: 'Duplicate Topic',
                text: 'This topic already exists. Please provide another name.',
            });
            return;
        }
        try {
            const payload = {
                id: editData.id,
                exam_id: parseInt(editData.exam_id),
                subject_id: parseInt(editData.subject_id),
                title: editData.title,
                status: editData.status // already 1 or 0 from the dropdown
            };

            // const res = await axios.post(`${API_URL}/api/topics/${editData.id}`, payload);
            const res = await api.post(`/topics/${editData.id}`, payload);

            if (res.status === 200 || res.status === 201) {
                // Find names from exam and subject arrays
                const exam = exams.find(e => e.id === payload.exam_id);
                const subject = subjects.find(s => s.id === payload.subject_id);

                const updated = {
                    ...editData,
                    exam_id: payload.exam_id,
                    subject_id: payload.subject_id,
                    examName: exam?.title || 'N/A',
                    subjectName: subject?.name || 'N/A',
                    status: payload.status === 1 ? 'Active' : 'Inactive'
                };

                setTopics(prev => prev.map(t => (t.id === editData.id ? updated : t)));
                setEditModalOpen(false);

                // ✅ SweetAlert for successful edit
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'The topic was successfully updated.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            if (err.response?.status === 409) {
                Swal.fire({
                    icon: 'error',
                    title: 'Duplicate Topic',
                    text: err.response.data.message || 'This topic already exists.',
                });
            } else {
                console.error(err);
                Swal.fire('Error', 'Failed to update topic', 'error');
            }
        }
    };

    //function for handling toggle
    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'Active' ? 0 : 1;

        try {
            const payload = { status: newStatus };

            //const res = await axios.put(`${API_URL}/api/topics/${id}`, payload); // <-- use PUT here
            const res = await api.put(`/topics/${id}`, payload);

            if (res.status === 200 || res.status === 201) {
                setTopics(prev =>
                    prev.map(topic =>
                        topic.id === id
                            ? { ...topic, status: newStatus === 1 ? 'Active' : 'Inactive' }
                            : topic
                    )
                );
                Swal.fire('Success', `Topic marked as ${newStatus ? 'Active' : 'Inactive'}`, 'success');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to update topic status', 'error');
        }
    };


    const columns = [
        { field: 'id', headerName: 'Topic ID', flex: 0.5 },
        { field: 'examName', headerName: 'Exam Name', flex: 1 },
        { field: 'subjectName', headerName: 'Subject Name', flex: 1 },
        { field: 'title', headerName: 'Topic Name', flex: 1.5 },
        { field: 'status', headerName: 'Status', flex: 0.5 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Switch
                        checked={params.row.status === 'Active'}
                        onChange={() => handleStatusToggle(params.row.id, params.row.status)}
                        color="primary"
                    />
                    <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                            ✏️
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
                            <FiTrash2 />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport />
        </GridToolbarContainer>
    );

    return (
        <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Box
                mb={3}
                display="flex"
                flexDirection={isSmall ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                textAlign={isSmall ? 'center' : 'left'}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                            transform: 'scale(1.05)'
                        }
                    }}
                >
                    <FiArrowLeft />
                    {!isSmall && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                </IconButton>

                <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiBookOpen />
                    </Box>
                    <Typography
                        variant={isSmall ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            px: 1
                        }}
                    >
                        Manage Topics
                    </Typography>
                </Box>

                <Button
                    fullWidth={isSmall}
                    variant="contained"
                    color="warning"
                    startIcon={<FiPlus />}
                    onClick={() => setAddModalOpen(true)}
                >
                    Add Topic
                </Button>
            </Box>

            <Box height={800} sx={{ flexGrow: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight="0" flex="1">
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={filteredTopics}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        components={{ Toolbar: CustomToolbar }}
                        loading={loading}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        sx={{
                            fontFamily: 'Segoe UI',
                            border: '1px solid #ddd',
                            boxShadow: 2,
                            minHeight: 0,
                            '& .MuiDataGrid-columnHeaders': {
                                background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
                                color: '#004c99',
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fef7e0'
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: '0.95rem'
                            }
                        }}
                    />
                )}
            </Box>

{/* calling that add topic modal  */}
            <TopicModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                newTopic={newTopic}
                setNewTopic={setNewTopic}
                exams={exams}
                filteredSubjects={subjects}
                handleAddTopic={handleAddTopic}
            />


{/* this dialog is for edit */}
            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1976d2', display: 'flex', gap: '8px', alignItems: 'center' }}
                    >
                        ✏️ Edit Topic
                    </motion.div>

                    <IconButton
                        aria-label="close"
                        onClick={() => setEditModalOpen(false)}
                        sx={{ position: 'absolute', top: 8, right: 8, color: 'red' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Exam Dropdown */}
                    <TextField
                        select
                        fullWidth
                        label="Exam"
                        name="exam_id"
                        value={editData.exam_id}
                        onChange={(e) =>
                            setEditData({ ...editData, exam_id: parseInt(e.target.value) })
                        }
                        margin="dense"
                    >
                        {exams.map((exam) => (
                            <MenuItem key={exam.id} value={exam.id}>
                                {exam.title || exam.name}
                            </MenuItem>
                        ))}

                    </TextField>

                    {/* Subject Dropdown */}
                    <TextField
                        select
                        fullWidth
                        label="Subject"
                        name="subject_id"
                        value={editData.subject_id}
                        onChange={(e) =>
                            setEditData({ ...editData, subject_id: parseInt(e.target.value) })
                        }
                        margin="dense"
                    >
                        {filteredSubjects.length > 0 ? (
                            filteredSubjects.map((subject) => (
                                <MenuItem key={subject.id} value={subject.id}>
                                    {subject.name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled value="">
                                {editData.exam_id ? 'No subjects available' : 'Select exam first'}
                            </MenuItem>
                        )}
                    </TextField>

                    {/* topic title field */}
                    <TextField
                        fullWidth
                        label="Topic Title"
                        name="title"
                        value={editData.title}
                        onChange={(e) =>
                            setEditData({ ...editData, title: e.target.value })
                        }
                        margin="dense"
                    />

                    {/* status dropdown */}
                    <TextField
                        select fullWidth label="Status"
                        margin="dense"
                        value={editData?.status ?? 1}
                        onChange={(e) => setEditData({ ...editData, status: parseInt(e.target.value) })}
                    >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                    </TextField>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" 
                    onClick={handleSaveChanges}>Save changes</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setToastOpen(false)} sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TopicList;
